import SearchForm from './../searchForm'
import React, {useEffect, useState, useRef} from "react";
import {useFormContext} from 'react-hook-form';
import {DELIVERY_FORM, DeliveryFormFields} from '../../index';
import IconButton from '@mui/material/IconButton';
import Typography from "@mui/material/Typography";
import {Trans, useTranslation} from "react-i18next";
import Link from "@mui/material/Link";
import {formatCopy} from "utils/typography";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import RouteCard from '../card';
import Pagination from '@mui/material/Pagination';
import Divider from "@mui/material/Divider";
import {
  Filters,
  Preloader,
  Minimize,
  Add,
  LinkPopover,
} from "@vedmarket/ui-kit";
import moment from "moment";
import { reachGoal } from 'utils/metrica';

import './modalSearch.scss';
import {useCurrentRoute, useGetRoutesMutation, useParamsData} from "../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import {ADORNMENTS, COLORS, DELIVERY_TIME_FILTER} from "../../../../../../../config/constants";
import { LocalShipping, Flight, DirectionsBoat, Train } from "@vedmarket/ui-kit";
import {useLazyGetDeliveryTimeQuery} from "../../../../../../../api/delivery_time";
import {
  getDemoDeliveryCostRange,
  getDemoVisibleDeliveryRoutes,
  shouldShowDemoCurrentRoute,
} from "components/cabinet/demo/steps/delivery/use-delivery";

export const enum SORTING_OPTIONS {
    DELIVERY_DESK = 'shipping_time,id',
    DELIVERY_ASC = '-shipping_time,id',
    PRICE_DESK = 'total_price,id',
    PRICE_ASC = '-total_price,id',
}

export const enum RESULTS_COUNT {
    COUNT_10 = 10,
    COUNT_20 = 20,
    COUNT_45 = 45
}

enum DELIVERY_TYPE {
  SEA = 'sea',
  AUTO = 'auto',
  AVIA = 'avia',
  RAILS = 'rails',
}

const DEFAULT_SHIPPING_METHODS = [
  DELIVERY_TYPE.AUTO,
  DELIVERY_TYPE.RAILS,
  DELIVERY_TYPE.AVIA,
  DELIVERY_TYPE.SEA,
]

const ModalSearch = ({preloadRoutes, refetch, handleRemove, handleClose, initialFilters} : DeliveryModalSearchProps ) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const [ filtersCollapsed, setFiltersCollapsed ] = useState(false);

  const [ routes, setRoutes ] = React.useState<Route[]>(preloadRoutes?.results || []);
  const [ totalRoutes, setTotalRoutes ] = React.useState(preloadRoutes?.results.length);
  const [ minTotal, setMinTotal ] = React.useState(0);
  const [ maxTotal, setMaxTotal ] = React.useState(10000);
  const [ filtersValues, setFiltersValues ] = React.useState<DeliverySearchFilterValues>(initialFilters || {
    cost: [ 0, 10000 ],
    deliveryDate: [ null, null ],
    shippingTime: DELIVERY_TIME_FILTER.ANY,
    shippingMethod: DEFAULT_SHIPPING_METHODS,
  });

  const filterData = useParamsData();
  const {getValues} = useFormContext<DeliveryFormFields>();

  const [ showProgressBarByDefault, setShowProgressBarByDefault ] = useState(true);
  const [ filtersAreApplying, setFiltersAreApplying ] = useState(false);

  const [ getRoutes, { isLoading } ] = useGetRoutesMutation()
  const [ deliveryTimeRequest, { data: deliveryTimeData } ] = useLazyGetDeliveryTimeQuery()

  const currentRoute = useCurrentRoute()

  const currentRouteRef = useRef<HTMLDivElement>(null);

  const getNormalizedMinTotal = (value?: number | null) => {
    if (typeof value === 'number') {
      return value;
    }

    return 0;
  }

  const getNormalizedMaxTotal = (value?: number | null) => {
    if (typeof value === 'number') {
      return value;
    }

    return 10000;
  }

  useEffect(() => {
    deliveryTimeRequest('')
  }, [ deliveryTimeRequest ])

  useEffect(() => {
    if (preloadRoutes?.results){
      setRoutes(preloadRoutes?.results);
      setTotalRoutes(preloadRoutes?.count || preloadRoutes?.results.length);
    }
    if (preloadRoutes){
      const responseMinTotal = getNormalizedMinTotal(preloadRoutes.meta?.min_total_price);
      const responseMaxTotal = getNormalizedMaxTotal(preloadRoutes.meta?.max_total_price);
      const [ nextMinTotal, nextMaxTotal ] = getDemoDeliveryCostRange(
        currentRoute,
        responseMinTotal,
        responseMaxTotal,
      );

      setMinTotal(nextMinTotal);
      setMaxTotal(nextMaxTotal)
      setFiltersValues((state) => ({
        ...state,
        cost: initialFilters?.cost || [ nextMinTotal, nextMaxTotal ],
      }));
    }
  }, [ currentRoute, initialFilters, preloadRoutes ])

  useEffect(() => {
    setTimeout(() => {
      setShowProgressBarByDefault(false)
    }, 1000);
  }, [ ])

  const routersRequest = (data?: DeliveryRouteParamsMain) => {
    return getRoutes({
      orderId,
      ...filterData,
      ...data,
    }).then((data) => {
      // @ts-ignore
      setRoutes(data.data.results);
      // @ts-ignore
      setTotalRoutes(data.data.count);
    });
  }

  const handleChangeResultsCount = (value: string | RESULTS_COUNT ) => routersRequest({
    limit: Number(value)
  });

  const handleChangeCurrentPage = (event: React.ChangeEvent<unknown>, pageNumber: number) => routersRequest({
    page: pageNumber
  });

  const handleSendSearch = (data: DeliveryRouteParamsMain) => routersRequest({
    ...data,
  });

  const renderCurrentRoute = () => {
    if (!currentRoute || !shouldShowDemoCurrentRoute(currentRoute.id)) {
      return;
    }

    const searchedCurrentRoute = routes.find((route) => route.id === currentRoute.id);
    const displayedCurrentRoute = {
      ...currentRoute,
      min_days: currentRoute.min_days ?? searchedCurrentRoute?.min_days,
      max_days: currentRoute.max_days ?? searchedCurrentRoute?.max_days,
    };

    return (<div ref={currentRouteRef}>
      <Divider/>
      <Typography variant='h6' sx={{
        marginTop: '24px',
        marginBottom: '24px',
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: '26px',
        letterSpacing: 0,
        color: COLORS.BLACK_DARK,
      }}>
        {t('calc__text__delivery__yourChoice')}
      </Typography>
      <RouteCard
        isActive
        isSearchResult
        route={displayedCurrentRoute}
        refetch={refetch}
        handleRemove={handleRemove}
      />
      <Divider sx={{ marginTop: 3, marginBottom: 3}} />
    </div>)
  };

  const resultsCount = filterData.limit || RESULTS_COUNT.COUNT_20;

  const handleChangeFilters = (values: DeliverySearchFilterValues) => {
    setFiltersValues(values);
  }

  const getFormattedDeliveryDate = (value: any) => {
    if (!value) {
      return undefined;
    }

    const formattedValue = moment(value).format('YYYY-MM-DD');

    return formattedValue === 'Invalid date' ? undefined : formattedValue;
  }

  const availableDeliveryTimes = deliveryTimeData?.map((item) => ({
    value: item.code,
    label: item.name,
  })) || [];

  const deliveryTimeOptions = [
    { value: DELIVERY_TIME_FILTER.ANY, label: t('calc__text__delivery__anyShippingTime') },
    ...availableDeliveryTimes,
  ];

  const renderRoutes = () => {
    if (filtersAreApplying) {
      return <Preloader />
    }

    const visibleRoutes = getDemoVisibleDeliveryRoutes(routes, currentRoute?.id)

    return visibleRoutes.map((route) =>
      <RouteCard
        isSearchResult
        route={route}
        key={route.id}
        refetch={refetch}
        handleRemove={handleRemove}
        handleClose={handleClose}
      />)
  }

  const handleApplyFilters = (values: DeliverySearchFilterValues) => {
    reachGoal({
      goal: 'primenit_filtr_m',
      params: {
        location_from: getValues(DELIVERY_FORM.LOCATION_FROM),
        location_to: getValues(DELIVERY_FORM.LOCATION_TO),
        planned_shipment_date: getValues(DELIVERY_FORM.PLANNED_SHIPMENT_DATE),
        cost: values.cost,
        deliveryDate: values.deliveryDate.map(value => value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : null),
        shippingTime: values.shippingTime,
        shippingMethod: values.shippingMethod,
        ...(totalRoutes ? {
          order_by: filterData.order_by || SORTING_OPTIONS.DELIVERY_DESK,
          limit: resultsCount,
        } : {}),
      },
    });

    const isDefaultCost = values.cost[0] === minTotal && values.cost[1] === maxTotal;
    const hasDefaultShippingMethods = values.shippingMethod.length === DEFAULT_SHIPPING_METHODS.length
      && DEFAULT_SHIPPING_METHODS.every((item) => values.shippingMethod.includes(item));

    setFiltersAreApplying(true);

    routersRequest({
      price_total_min: isDefaultCost ? undefined : values.cost[0],
      price_total_max: isDefaultCost ? undefined : values.cost[1],
      shipping_time: values.shippingTime === DELIVERY_TIME_FILTER.ANY ? '' : values.shippingTime,
      shipping_method: hasDefaultShippingMethods ? undefined : values.shippingMethod,
      delivery_date_from: getFormattedDeliveryDate(values.deliveryDate[0]),
      delivery_date_to: getFormattedDeliveryDate(values.deliveryDate[1]),
    }).finally(() => {
      setFiltersAreApplying(false);
    });

  }

  return (
    <div className='delivery-modal-search'>
      <SearchForm isBigInputs onSend={handleSendSearch} />
      <div className='delivery-results'>
        <Typography variant='h6' sx={{
          marginBottom: '24px',
          fontSize: '24px',
          fontWeight: 700,
          lineHeight: 1.28,
          minHeight: '31px',
          letterSpacing: 0,
          color: COLORS.BLACK_DARK,
        }}>
          {t('common__text__searchResults')}
        </Typography>
        {!showProgressBarByDefault && <div className='delivery-results__container'>
          {Boolean(totalRoutes) && <div className='delivery-results__results'>
            <div className='delivery-results__sorting'>
              <FormControl className='delivery-results__sort-control'>
                <InputLabel id='sorting-select-label' shrink>{t('common__text__sort')}</InputLabel>
                <Select
                  labelId='sorting-select-label'
                  id='sorting-select'
                  value={filterData.order_by || SORTING_OPTIONS.DELIVERY_DESK}
                  label={t('common__text__sort')}
                  onChange={({ target: { value } }) => routersRequest({
                    order_by: value
                  })}
                  className='delivery-results__select-sorting'>
                  <MenuItem value={SORTING_OPTIONS.DELIVERY_DESK}>{t('calc__text__delivery__sortShippingTimeAsc')}</MenuItem>
                  <MenuItem value={SORTING_OPTIONS.DELIVERY_ASC}>{t('calc__text__delivery__sortShippingTimeDesc')}</MenuItem>
                  <MenuItem value={SORTING_OPTIONS.PRICE_DESK}>{t('calc__text__delivery__sortPriceAsc')}</MenuItem>
                  <MenuItem value={SORTING_OPTIONS.PRICE_ASC}>{t('calc__text__delivery__sortPriceDesc')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl className='delivery-results__count-control'>
                <InputLabel id='count-select-label' shrink>{t('common__text__resultsCount')}</InputLabel>
                <Select
                  labelId='count-select-label'
                  id='count-select'
                  value={resultsCount}
                  label={t('common__text__resultsCount')}
                  onChange={({ target: { value } }) => handleChangeResultsCount(value)}
                  className='delivery-results__select-count'>
                  <MenuItem value={RESULTS_COUNT.COUNT_10}>10</MenuItem>
                  <MenuItem value={RESULTS_COUNT.COUNT_20}>20</MenuItem>
                  <MenuItem value={RESULTS_COUNT.COUNT_45}>45</MenuItem>
                </Select>
              </FormControl>
            </div>
            {renderCurrentRoute()}
            {renderRoutes()}
            {!filtersAreApplying && Number(totalRoutes) > resultsCount && <Pagination
              count={Math.ceil(Number(totalRoutes) / resultsCount)}
              sx={{ marginTop: '24px' }}
              boundaryCount={2}
              siblingCount={0}
              size='large'
              onChange={handleChangeCurrentPage}
            />}
          </div>}
          {!totalRoutes && <>
            <div className='delivery-results__results'>
              {renderCurrentRoute()}
              {!currentRoute?.id && <Divider sx={{ marginBottom: 3, width: '100%' }} />}
              <Typography variant='h6' gutterBottom sx={{ marginTop: 4}}>
                {t('actions__message__searchNotFound')}
              </Typography>
              {!isLoading && !filtersAreApplying && <LinkPopover
                linkText={t('calc__help__noDeliveryOptionsLink')}
                text={<Trans
                  defaults={formatCopy(t('calc__help__noDeliveryOptions'))}
                  components={{
                    support: <Link
                      href='mailto:demo@vedmarket.ru'
                      variant='inherit'
                      underline='hover'
                      sx={{
                        color: COLORS.BLUE,
                        cursor: 'pointer',
                      }}
                    />,
                  }}
                />}
              />}
            </div>
          </>
          }
          <Card className='delivery-results__filters' elevation={0} square>
            <CardContent sx={{
              padding: 0,
              '&:last-child': {
                paddingBottom: 0
              }
            }}>
              <div className='delivery-filter__header'>
                <Typography variant='h6' sx={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '32px',
                  letterSpacing: 0,
                  color: COLORS.BLACK_DARK,
                }}>
                  {t('common__text__filters')}
                </Typography>
                <IconButton
                  aria-label={t(filtersCollapsed ? 'calc__text__delivery__showFilters' : 'calc__text__delivery__hideFilters')}
                  aria-expanded={!filtersCollapsed}
                  onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                  sx={{ padding: 0, color: COLORS.LIGHT_ACTION }}
                >
                  {filtersCollapsed ? <Add /> : <Minimize />}
                </IconButton>
              </div>
              <div hidden={filtersCollapsed}>
                <Filters
                  applyButtonText={t('actions__text__applyFilters')}
                  onApply={(values) => handleApplyFilters(values as DeliverySearchFilterValues)}
                  onChange={(values) => handleChangeFilters(values as DeliverySearchFilterValues)}
                  sections={[ {
                    type: 'date-range',
                    key: 'deliveryDate',
                    title: t('calc__text__delivery__arrivalDate'),
                    label: t('calc__text__delivery__arrivalDates'),
                    defaultValues: [ null, null ],
                    selectedValues: filtersValues.deliveryDate,
                    disablePast: true,
                  }, {
                    type: 'select',
                    key: 'shippingTime',
                    title: t('calc__text__delivery__shippingTime'),
                    label: t('calc__text__delivery__shippingTime'),
                    defaultValues: DELIVERY_TIME_FILTER.ANY,
                    selectedValues: filtersValues.shippingTime,
                    options: deliveryTimeOptions,
                  }, {
                    type: 'range',
                    key: 'cost',
                    title: t('common__text__cost'),
                    defaultValues: [ minTotal, maxTotal ],
                    min: minTotal,
                    max: maxTotal,
                    selectedValues: filtersValues.cost,
                    fromLabel: t('calc__text__delivery__filterCostFrom'),
                    toLabel: t('calc__text__delivery__filterCostTo'),
                    valueSuffix: 'USD',
                    inputAdornment: ADORNMENTS.PRICE,
                  }, {
                    key: 'shippingMethod',
                    title: t('calc__text__packaging__deliveryMethod'),
                    defaultValues: DEFAULT_SHIPPING_METHODS,
                    selectedValues: filtersValues.shippingMethod,
                    options: [
                      {
                        value: DELIVERY_TYPE.AUTO,
                        label: <span className='delivery-filter__label'><LocalShipping className='delivery-filter__icon' color={COLORS.LIGHT_TEXT_DISABLED}/><Typography component='span' variant='inherit'>{t('calc__text__delivery__automotive')}</Typography></span>,
                      },
                      {
                        value: DELIVERY_TYPE.RAILS,
                        label: <span className='delivery-filter__label'><Train className='delivery-filter__icon' color={COLORS.LIGHT_TEXT_DISABLED}/><Typography component='span' variant='inherit'>{t('calc__text__delivery__railway')}</Typography></span>,
                      },
                      {
                        value: DELIVERY_TYPE.AVIA,
                        label: <span className='delivery-filter__label'><Flight className='delivery-filter__icon' color={COLORS.LIGHT_TEXT_DISABLED}/><Typography component='span' variant='inherit'>{t('calc__text__delivery__filterAir')}</Typography></span>,
                      },
                      {
                        value: DELIVERY_TYPE.SEA,
                        label: <span className='delivery-filter__label'><DirectionsBoat className='delivery-filter__icon' color={COLORS.LIGHT_TEXT_DISABLED}/><Typography component='span' variant='inherit'>{t('calc__text__delivery__water')}</Typography></span>,
                      },
                    ],
                  } ]}
                />
              </div>
            </CardContent>
          </Card>
        </div>}
      </div>
    </div>
  )
}

export default ModalSearch
